import { posClient } from './client';
import {
  normalizeManagedTable,
  normalizeTabItem,
  normalizeTable,
  wireFloorSchema,
  wireManagedFloorSchema,
  wireManagedTableMutationSchema,
  wireTabDetailSchema,
  wireTabMutationSchema,
  type ManagedTable,
  type RestaurantTable,
  type TabDetail,
  type TableDraft,
} from '@/types/tables';
import {
  normalizeCreatedOrder,
  normalizePaymentSummary,
  normalizeRound,
  wireCreatedOrderSchema,
  type CompletedOrder,
  type OrderRound,
  type PaymentSummary,
} from '@/types/checkout';

/**
 * Restaurant floor + tab adapters (V1 fast-follow).
 *
 * Route evidence: pos-api `routes/api.php` — `GET /tables`,
 * `GET|POST /tables/{id}/tab`, `POST /orders/{id}/items`,
 * `POST /orders/{id}/bill-requested`, `POST /orders/{id}/settle`.
 *
 * The three mutations return the upstream order body, but this module reads
 * only its id and returns void. That mirrors `ordersApi.updateStatus`: the
 * console re-fetches after a write instead of trusting a write response to
 * describe the resulting state. It is what keeps the floor correct when two
 * devices act on the same table at once.
 */

export interface TableRequestOptions {
  signal?: AbortSignal;
}

/** One line a waiter is adding. Mirrors the checkout item shape. */
export interface TabItemInput {
  product_id: number;
  quantity: number;
  variant_id?: number | null;
  attributes?: Record<string, unknown>;
  parent_key?: string;
}

export type SettlementMethod = 'cash' | 'card' | 'mobile';

/**
 * What a tab mutation gives back.
 *
 * Most of it is still deliberately thin — the console re-fetches rather than
 * trusting a write response to describe the floor, which is what keeps two
 * devices working the same section consistent. The two exceptions exist because
 * nothing else can supply them: `round` names the items the kitchen has just
 * been given, and `order`/`payment` on settlement are the receipt.
 */
export interface TabMutationResult {
  orderId: number | null;
  round: OrderRound | null;
  payment: PaymentSummary | null;
  /** Settlement only — the authoritative settled order, for the receipt. */
  order?: CompletedOrder | null;
}

export const tablesApi = {
  /**
   * GET {pos}/tables — the floor for a branch, each row carrying its open tab
   * summary (identifiers, counts and totals only; no guest PII).
   */
  async floor(branchId: number, options: TableRequestOptions = {}): Promise<RestaurantTable[]> {
    const res = await posClient.get('/tables', {
      params: { branch_id: branchId },
      signal: options.signal,
    });
    return wireFloorSchema.parse(res.data).data.tables.map(normalizeTable);
  },

  /**
   * GET {pos}/tables/{id}/tab — the open tab with its line items, or null when
   * the table is free.
   *
   * This exists because `GET /orders/{id}` is `pos.role:sell` and a waiter is
   * not a seller; it is scoped to one table's open tab.
   */
  async tab(tableId: number, options: TableRequestOptions = {}): Promise<TabDetail | null> {
    const res = await posClient.get(`/tables/${tableId}/tab`, { signal: options.signal });
    const body = wireTabDetailSchema.parse(res.data).data;

    if (!body.order) return null;

    const order = body.order;
    return {
      tableId: body.table_id,
      orderId: order.id,
      orderNumber: order.order_number ?? null,
      status: order.status,
      subTotal: order.sub_total ?? '0',
      discountAmount: order.discount_amount ?? '0',
      totalAmount: order.total_amount ?? '0',
      notes: order.notes ?? null,
      covers: order.covers ?? null,
      billRequestedAt: order.bill_requested_at ?? null,
      openedByUserId: order.opened_by_user_id ?? null,
      openedAt: order.opened_at ?? null,
      items: order.items.map(normalizeTabItem),
    };
  },

  /**
   * POST {pos}/tables/{id}/tab — seat the table, optionally with its first round.
   *
   * Sending `items` here makes the table claim, the pricing, the stock movement
   * and round 1 ONE transaction upstream (V2.2-04). The waiter who takes the
   * order at the table used to need two requests — open, then append — and the
   * gap between them is where a seated-but-empty tab with no kitchen ticket
   * came from.
   *
   * Returns the new order id and the accepted round. 409 means another device
   * seated this table first; callers refresh the floor rather than retry.
   */
  async openTab(
    tableId: number,
    body: {
      covers?: number;
      notes?: string;
      daySessionId?: number | null;
      items?: TabItemInput[];
      clientOrderId?: string;
      promotionCodes?: string[];
      customerId?: number;
    } = {},
    options: TableRequestOptions = {},
  ): Promise<TabMutationResult> {
    const payload: Record<string, unknown> = {};
    if (body.covers != null) payload.covers = body.covers;
    if (body.notes) payload.notes = body.notes;
    if (body.daySessionId != null) payload.day_session_id = body.daySessionId;
    if (body.items?.length) payload.items = body.items;
    if (body.clientOrderId) payload.client_order_id = body.clientOrderId;
    if (body.promotionCodes?.length) payload.promotion_codes = body.promotionCodes;
    if (body.customerId != null) payload.customer_id = body.customerId;

    const res = await posClient.post(`/tables/${tableId}/tab`, payload, { signal: options.signal });
    const parsed = wireTabMutationSchema.parse(res.data);

    return {
      orderId: parsed.order?.id ?? null,
      round: parsed.round ? normalizeRound(parsed.round) : null,
      payment: parsed.payment ? normalizePaymentSummary(parsed.payment) : null,
    };
  },

  /**
   * POST {pos}/orders/{id}/items — add a round to an open tab.
   *
   * Prices are NOT sent: the server reads them from the product table, which is
   * what keeps a single pricing implementation.
   *
   * `clientRoundId` is the round's operation key. Replaying it returns the
   * ORIGINAL round instead of appending again, which is what makes a retry
   * after a lost response safe rather than a second helping on the guest's tab.
   */
  async addItems(
    orderId: number,
    items: TabItemInput[],
    options: TableRequestOptions & { clientRoundId?: string } = {},
  ): Promise<TabMutationResult> {
    const res = await posClient.post(
      `/orders/${orderId}/items`,
      { items, ...(options.clientRoundId ? { client_round_id: options.clientRoundId } : {}) },
      { signal: options.signal },
    );
    const parsed = wireTabMutationSchema.parse(res.data);

    return {
      orderId: parsed.order?.id ?? orderId,
      round: parsed.round ? normalizeRound(parsed.round) : null,
      payment: null,
    };
  },

  /**
   * POST {pos}/orders/{id}/bill-requested — flag that the guest asked for the
   * bill. Idempotent upstream: the first request sets the timestamp and later
   * ones leave it alone.
   */
  async requestBill(orderId: number, options: TableRequestOptions = {}): Promise<void> {
    const res = await posClient.post(
      `/orders/${orderId}/bill-requested`,
      {},
      { signal: options.signal },
    );
    wireTabMutationSchema.parse(res.data);
  },

  /**
   * POST {pos}/orders/{id}/fire — send the tab to the kitchen.
   *
   * There is no per-item firing upstream, so this advances the whole tab's
   * order status. It is a dedicated route rather than the generic status
   * endpoint because that one is `sell`-gated; this is `tables.fire`, so a
   * waiter can send their own table.
   */
  async fire(orderId: number, options: TableRequestOptions = {}): Promise<void> {
    const res = await posClient.post(`/orders/${orderId}/fire`, {}, { signal: options.signal });
    wireTabMutationSchema.parse(res.data);
  },

  /**
   * POST {pos}/orders/{id}/settle — close the tab and free the table.
   *
   * `paymentMethod` is a manually verified label, exactly as at checkout:
   * there is no gateway, no capture and no reconciliation behind it. Requires
   * `pos.role:sell`, so a waiter-only account gets 403 here by design.
   *
   * `cashReceived` is required for cash. Change is calculated upstream against
   * the LOCKED tab total and returned in `payment` — a client-computed change
   * figure is a number nobody verified.
   *
   * Unlike the other tab mutations this one DOES return its response body: the
   * settled order is what the receipt prints, and discarding it (as the console
   * used to) is why a closed tab could never produce one.
   */
  async settle(
    orderId: number,
    paymentMethod: SettlementMethod,
    options: TableRequestOptions & { notes?: string; cashReceived?: number } = {},
  ): Promise<TabMutationResult> {
    const body: Record<string, unknown> = { payment_method: paymentMethod };
    if (options.notes) body.notes = options.notes;
    if (paymentMethod === 'cash' && options.cashReceived != null) {
      body.cash_received = options.cashReceived;
    }

    const res = await posClient.post(`/orders/${orderId}/settle`, body, { signal: options.signal });
    const parsed = wireTabMutationSchema.parse(res.data);

    // Parsed against the FULL order schema separately, because the shared
    // mutation schema keeps `order` loose for every other caller. safeParse:
    // an unexpected receipt shape must not throw away a settlement that has
    // already happened — the tab is closed either way, and the console can
    // still re-fetch. Only the printed copy is lost.
    const settled = wireCreatedOrderSchema.safeParse(parsed.order);

    return {
      orderId: parsed.order?.id ?? orderId,
      round: null,
      payment: parsed.payment ? normalizePaymentSummary(parsed.payment) : null,
      order: settled.success ? normalizeCreatedOrder(settled.data) : null,
    };
  },
};

/* ----------------------------------------------------- floor plan (office) */

/**
 * Only the fields the caller actually set. `undefined` means "leave alone",
 * `null` means "clear it" — the difference matters for `section`, where those
 * two are a real distinction and not a nicety. The server's rules are all
 * `sometimes`, so an absent key is genuinely untouched rather than defaulted.
 */
function draftPayload(draft: TableDraft): Record<string, unknown> {
  const payload: Record<string, unknown> = {};

  if (draft.label !== undefined) payload.label = draft.label;
  if (draft.floorId !== undefined) payload.floor_id = draft.floorId;
  if (draft.section !== undefined) payload.section = draft.section;
  if (draft.seats !== undefined) payload.seats = draft.seats;
  if (draft.sortOrder !== undefined) payload.sort_order = draft.sortOrder;
  if (draft.isActive !== undefined) payload.is_active = draft.isActive;

  return payload;
}

/**
 * Floor-plan administration (`tables.manage`, manager and up).
 *
 * Every branch is passed explicitly rather than leaning on the ambient
 * X-Branch header, because the office is the one place that legitimately
 * administers a floor it is not standing on. The server accepts either and
 * prefers the explicit one.
 *
 * There is deliberately no delete: a table is taken out of service by retiring
 * it (`isActive: false`), and pos-api exposes no DELETE route at all. Orders
 * point at tables with `nullOnDelete` upstream, so deleting one would cut every
 * finished order loose from the table that served it.
 *
 * The writes return void by the same rule the tab mutations follow above: the
 * console re-fetches rather than trusting a write response to describe the
 * resulting state. Here it also avoids a specific trap — a write response
 * carries no `has_open_tab`, so folding one into the list would quietly clear
 * the flag the retire control depends on.
 */
export const tablesAdminApi = {
  /** GET {pos}/tables/manage — every table on a branch, retired ones included. */
  async floorPlan(branchId: number, options: TableRequestOptions = {}): Promise<ManagedTable[]> {
    const res = await posClient.get('/tables/manage', {
      params: { branch_id: branchId },
      signal: options.signal,
    });
    return wireManagedFloorSchema.parse(res.data).data.tables.map(normalizeManagedTable);
  },

  /**
   * POST {pos}/tables — add a table.
   *
   * 422 with a `label` error means the branch already has a table by that
   * name; the pair is unique per branch, not globally.
   */
  async create(
    branchId: number,
    draft: TableDraft & { label: string },
    options: TableRequestOptions = {},
  ): Promise<void> {
    const res = await posClient.post(
      '/tables',
      { ...draftPayload(draft), branch_id: branchId },
      { signal: options.signal },
    );
    wireManagedTableMutationSchema.parse(res.data);
  },

  /**
   * PUT {pos}/tables/{id} — rename, re-section, reseat, retire or restore.
   *
   * 409 means the table still has an open tab and cannot be retired.
   */
  async update(
    branchId: number,
    tableId: number,
    draft: TableDraft,
    options: TableRequestOptions = {},
  ): Promise<void> {
    const res = await posClient.put(`/tables/${tableId}`, draftPayload(draft), {
      params: { branch_id: branchId },
      signal: options.signal,
    });
    wireManagedTableMutationSchema.parse(res.data);
  },

  /**
   * POST {pos}/tables/reorder — rewrite sort_order across a floor at once.
   *
   * Sent as one request rather than one per moved table: a reorder touches
   * many rows, and a half-applied sequence is a floor plan nobody asked for.
   * The server verifies the whole batch before writing any of it.
   */
  async reorder(
    branchId: number,
    entries: { id: number; sortOrder: number }[],
    options: TableRequestOptions = {},
  ): Promise<void> {
    await posClient.post(
      '/tables/reorder',
      {
        branch_id: branchId,
        tables: entries.map((entry) => ({ id: entry.id, sort_order: entry.sortOrder })),
      },
      { signal: options.signal },
    );
  },
};
